import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Flex,
  Icon,
  useDisclosure,
  Box
} from '@chakra-ui/react';
import { createContext, useContext, ReactNode } from 'react';

// 创建一个Context用于全局控制弹窗
interface AlertContextType {
  showAlert: (title: string, message: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
}

const AlertContext = createContext<AlertContextType>({
  showAlert: () => {}
});

// 弹窗显示的Hook
export const useCustomAlert = () => useContext(AlertContext);

type AlertProviderProps = {
  children: ReactNode;
};

// 弹窗的Icon类型
const AlertIcons = {
  info: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="#3182CE" strokeWidth="2" />
      <path d="M12 8v.01M12 12v4" stroke="#3182CE" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  success: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="#38A169" strokeWidth="2" />
      <path d="M8 12l3 3 5-5" stroke="#38A169" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  warning: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"
        stroke="#ED8936"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M12 8v4M12 16v.01" stroke="#ED8936" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  error: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="#E53E3E" strokeWidth="2" />
      <path d="M15 9l-6 6M9 9l6 6" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
};

// 弹窗的主题颜色
const AlertColors = {
  info: 'blue',
  success: 'green',
  warning: 'orange',
  error: 'red'
};

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [alertConfig, setAlertConfig] = React.useState({
    title: '',
    message: '',
    type: 'info' as 'info' | 'success' | 'warning' | 'error'
  });

  const showAlert = (
    title: string,
    message: string,
    type: 'info' | 'success' | 'warning' | 'error' = 'info'
  ) => {
    setAlertConfig({ title, message, type });
    onOpen();
  };

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}
      <Modal isOpen={isOpen} onClose={onClose} isCentered motionPreset="scale">
        <ModalOverlay bg="rgba(0, 0, 0, 0.7)" backdropFilter="blur(5px)" />
        <ModalContent
          bg="rgba(30, 30, 40, 0.95)"
          backdropFilter="blur(10px)"
          borderRadius="xl"
          boxShadow="0 8px 32px rgba(0, 0, 0, 0.15)"
          color="white"
          borderWidth="1px"
          borderColor={`${AlertColors[alertConfig.type]}.500`}
          borderStyle="solid"
          maxW="sm"
        >
          <ModalHeader
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            pb={3}
            borderBottomWidth="1px"
            borderBottomColor="rgba(255, 255, 255, 0.1)"
          >
            <Flex align="center">
              <Box mr={3}>{AlertIcons[alertConfig.type]}</Box>
              <Text fontSize="lg" fontWeight="bold">
                {alertConfig.title}
              </Text>
            </Flex>
          </ModalHeader>

          <ModalBody py={6}>
            <Text fontSize="md" opacity={0.9}>
              {alertConfig.message}
            </Text>
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme={AlertColors[alertConfig.type]}
              borderRadius="full"
              px={6}
              onClick={onClose}
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
            >
              确定
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AlertContext.Provider>
  );
};

export default AlertProvider; 